import { useEffect, useMemo, useState } from 'react';
import ImageUploadField from '../common/ImageUploadField';
import { appendFormValue, appendFiles } from '../../utils/formData';
import { listCategories } from '../../services/categoryService';
import { listProducts } from '../../services/productService';

export const HERO_BG_COLORS = [
  { value: 'from-cyan-50 to-teal-50', label: 'Cyan / Teal' },
  { value: 'from-green-50 to-emerald-50', label: 'Green / Emerald' },
  { value: 'from-amber-50 to-orange-50', label: 'Amber / Orange' },
  { value: 'from-rose-50 to-pink-50', label: 'Rose / Pink' },
  { value: 'from-blue-50 to-indigo-50', label: 'Blue / Indigo' },
  { value: 'from-violet-50 to-purple-50', label: 'Violet / Purple' },
];

function getProductCategoryId(product) {
  if (!product?.category) return '';
  return typeof product.category === 'object' ? product.category._id : product.category;
}

function extractProductSlugFromLink(link) {
  const match = (link || '').match(/^\/products\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

export default function HeroSlideForm({ form, onChange }) {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const set = (field, value) => onChange({ ...form, [field]: value });

  useEffect(() => {
    let active = true;

    async function loadOptions() {
      setLoadingOptions(true);
      try {
        const [categoriesRes, productsRes] = await Promise.all([
          listCategories(),
          listProducts({ limit: 200 }),
        ]);
        if (!active) return;
        setCategories(categoriesRes.data || []);
        setProducts(productsRes.data || []);
      } finally {
        if (active) setLoadingOptions(false);
      }
    }

    loadOptions();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!products.length || form.categoryId || form.productId || !form.link) return;

    const slug = extractProductSlugFromLink(form.link);
    if (!slug) return;

    const product = products.find((item) => item.slug === slug);
    if (!product) return;

    onChange({
      ...form,
      categoryId: getProductCategoryId(product),
      productId: product._id,
    });
  }, [products, form.link, form.categoryId, form.productId]);

  const filteredProducts = useMemo(() => {
    if (!form.categoryId) return [];
    return products.filter((product) => getProductCategoryId(product) === form.categoryId);
  }, [products, form.categoryId]);

  const handleCategoryChange = (categoryId) => {
    onChange({
      ...form,
      categoryId,
      productId: '',
      link: '',
    });
  };

  const handleProductChange = (productId) => {
    const product = filteredProducts.find((item) => item._id === productId);
    onChange({
      ...form,
      productId,
      link: product ? `/products/${product.slug}` : '',
    });
  };

  return (
    <div className="form-grid">
      <div className="form-group full">
        <label>Title *</label>
        <input
          className="input"
          value={form.title || ''}
          onChange={(e) => set('title', e.target.value)}
          required
        />
      </div>
      <div className="form-group full">
        <label>Subtitle</label>
        <input
          className="input"
          value={form.subtitle || ''}
          onChange={(e) => set('subtitle', e.target.value)}
          placeholder="e.g. Premium Marine Collagen"
        />
      </div>
      <div className="form-group full">
        <label>Description</label>
        <textarea
          className="input textarea"
          rows={3}
          value={form.description || ''}
          onChange={(e) => set('description', e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>CTA Button Text</label>
        <input
          className="input"
          value={form.cta || ''}
          onChange={(e) => set('cta', e.target.value)}
          placeholder="Shop Now"
        />
      </div>
      <div className="form-group">
        <label>Category *</label>
        <select
          className="input"
          value={form.categoryId || ''}
          onChange={(e) => handleCategoryChange(e.target.value)}
          disabled={loadingOptions}
          required
        >
          <option value="">Select category</option>
          {categories.map((category) => (
            <option key={category._id} value={category._id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label>Product *</label>
        <select
          className="input"
          value={form.productId || ''}
          onChange={(e) => handleProductChange(e.target.value)}
          disabled={loadingOptions || !form.categoryId}
          required
        >
          <option value="">
            {!form.categoryId
              ? 'Select category first'
              : filteredProducts.length
                ? 'Select product'
                : 'No products in this category'}
          </option>
          {filteredProducts.map((product) => (
            <option key={product._id} value={product._id}>
              {product.name}
            </option>
          ))}
        </select>
      </div>
      {form.link ? (
        <div className="form-group full">
          <label>Link</label>
          <input className="input" value={form.link} readOnly />
        </div>
      ) : null}
      <div className="form-group">
        <label>Background Gradient</label>
        <select
          className="input"
          value={form.bgColor || HERO_BG_COLORS[0].value}
          onChange={(e) => set('bgColor', e.target.value)}
        >
          {HERO_BG_COLORS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label>Sort Order</label>
        <input
          type="number"
          min="0"
          className="input"
          value={form.sortOrder ?? 0}
          onChange={(e) => set('sortOrder', e.target.value)}
        />
      </div>
      <div className="form-group full">
        <label>Feature Bullets</label>
        <textarea
          className="input textarea"
          rows={3}
          value={form.featuresText || ''}
          onChange={(e) => set('featuresText', e.target.value)}
          placeholder={'One feature per line\ne.g. Improves skin elasticity'}
        />
      </div>
      <div className="form-group full">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={form.isActive !== false}
            onChange={(e) => set('isActive', e.target.checked)}
          />
          Active (visible on homepage)
        </label>
      </div>

      <ImageUploadField
        label="Slide Image *"
        existingUrls={form.existingImage ? [form.existingImage] : []}
        onExistingChange={(urls) => set('existingImage', urls[0] || '')}
        files={form.newImageFiles || []}
        onFilesChange={(files) => set('newImageFiles', files)}
      />
    </div>
  );
}

export const emptyHeroSlideForm = {
  title: '',
  subtitle: '',
  description: '',
  cta: 'Shop Now',
  categoryId: '',
  productId: '',
  link: '',
  bgColor: HERO_BG_COLORS[0].value,
  featuresText: '',
  sortOrder: 0,
  isActive: true,
  existingImage: '',
  newImageFiles: [],
};

export function heroSlideToForm(slide) {
  if (!slide) return { ...emptyHeroSlideForm };
  return {
    title: slide.title || '',
    subtitle: slide.subtitle || '',
    description: slide.description || '',
    cta: slide.cta || 'Shop Now',
    categoryId: '',
    productId: '',
    link: slide.link || '',
    bgColor: slide.bgColor || HERO_BG_COLORS[0].value,
    featuresText: (slide.features || []).join('\n'),
    sortOrder: slide.sortOrder ?? 0,
    isActive: slide.isActive !== false,
    existingImage: slide.image || '',
    newImageFiles: [],
  };
}

function parseFeatures(text) {
  return (text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

export function formToHeroSlideFormData(form) {
  const formData = new FormData();

  appendFormValue(formData, 'title', form.title);
  appendFormValue(formData, 'subtitle', form.subtitle);
  appendFormValue(formData, 'description', form.description);
  appendFormValue(formData, 'cta', form.cta || 'Shop Now');
  appendFormValue(formData, 'link', form.link);
  appendFormValue(formData, 'bgColor', form.bgColor);
  appendFormValue(formData, 'sortOrder', form.sortOrder ?? 0);
  appendFormValue(formData, 'isActive', form.isActive !== false);
  appendFormValue(formData, 'features', JSON.stringify(parseFeatures(form.featuresText)));
  appendFormValue(formData, 'existingImage', form.existingImage || '');
  appendFiles(formData, 'image', form.newImageFiles || []);

  return formData;
}
